export interface AdminCategory {
  id: string;
  name: string;
  shortDescription: string;
  image: string;
  active: boolean;
}

export interface AdminProduct {
  id: string;
  name: string;
  category: string;
  collection: string;
  shortDescription: string;
  fullDescription: string;
  price: number;
  promotionalPrice: number | null;
  sizes: string[];
  colors: string[];
  stock: number;
  active: boolean;
  featured: boolean;
  mainImage: string;
  gallery: string[];
  sku: string;
  internalNotes: string;
}

export interface AdminCollection {
  id: string;
  name: string;
  shortDescription: string;
  coverImage: string;
  active: boolean;
  featured: boolean;
}

export interface SiteTexts {
  heroTitle: string;
  heroSubtitle: string;
  heroPrimaryButton: string;
  heroSecondaryButton: string;
  heroImage: string;
  aboutTitle: string;
  aboutText: string;
  aboutHighlights: string[];
  finalCtaTitle: string;
  finalCtaText: string;
  finalCtaButton: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
}

export interface AdminBanner {
  id: string;
  title: string;
  subtitle: string;
  buttonText: string;
  buttonLink: string;
  image: string;
  location: 'principal' | 'colecao' | 'promocional' | 'final-whatsapp';
  active: boolean;
}

export interface StoreInfo {
  storeName: string;
  whatsapp: string;
  instagram: string;
  address: string;
  businessHours: string;
  serviceText: string;
  email: string;
  city: string;
  state: string;
  googleMapsLink: string;
  whatsappDefaultMessage: string;
}

export interface AdminOrder {
  id: string;
  customerName: string;
  date: string;
  products: { name: string; size: string; quantity: number; price: number }[];
  total: number;
  status: 'novo' | 'em-atendimento' | 'finalizado' | 'cancelado';
}

export interface AdminSettings {
  adminName: string;
  adminEmail: string;
  primaryColor: string;
  secondaryColor: string;
  showPrices: boolean;
  showStock: boolean;
  showFeatured: boolean;
  whatsappNumber: string;
  cartFinalMessage: string;
}

export const defaultCategories: AdminCategory[] = [
  { id: 'cat-1', name: 'Bebê', shortDescription: 'Roupas para os pequenos de 0 a 24 meses', image: '/img/categories/admin-cat-bebe.jpg', active: true },
  { id: 'cat-2', name: 'Menina', shortDescription: 'Looks encantadores para meninas', image: '/img/categories/admin-cat-menina.jpg', active: true },
  { id: 'cat-3', name: 'Menino', shortDescription: 'Estilo e conforto para meninos', image: '/img/categories/admin-cat-menino.jpg', active: true },
  { id: 'cat-4', name: 'Acessórios', shortDescription: 'Laços, tiaras e complementos', image: '/img/categories/admin-cat-acessorios.jpg', active: true },
  { id: 'cat-5', name: 'Lançamentos', shortDescription: 'Novidades da estação', image: '/img/categories/admin-cat-lancamentos.jpg', active: true },
  { id: 'cat-6', name: 'Coleções especiais', shortDescription: 'Edições limitadas e temáticas', image: '/img/categories/admin-cat-especiais.jpg', active: true },
];

export const defaultProducts: AdminProduct[] = [
  {
    id: 'prod-1', name: 'Body Algodão Bebê', category: 'cat-1', collection: 'col-1',
    shortDescription: 'Body de algodão orgânico, super macio para a pele sensível do bebê.',
    fullDescription: 'Nosso Body de Algodão Bebê é feito com 100% algodão orgânico certificado, garantindo o máximo de conforto para o seu pequeno. Com fechamento prático em botões de pressão, facilita a troca de fraldas. Disponível em cores neutras e estampas delicadas.',
    price: 49.90, promotionalPrice: null,
    sizes: ['RN', 'P', 'M', 'G'],
    colors: ['Branco', 'Rosa Claro', 'Azul Bebê'],
    stock: 25, active: true, featured: true,
    mainImage: '/img/products/admin-prod-body.jpg',
    gallery: [],
    sku: 'MCV-BDY-001',
    internalNotes: 'Best seller na categoria bebê.',
  },
  {
    id: 'prod-2', name: 'Vestido Floral Menina', category: 'cat-2', collection: 'col-2',
    shortDescription: 'Vestido leve com estampa floral, ideal para dias quentes.',
    fullDescription: 'Vestido confeccionado em tecido leve e respirável, com estampa floral exclusiva da Maçã Verde. Perfeito para passeios ao ar livre e ocasiões especiais. Possui forro interno para maior conforto e modelagem rodada que permite liberdade de movimento.',
    price: 89.90, promotionalPrice: 74.90,
    sizes: ['2', '4', '6', '8', '10'],
    colors: ['Floral Rosa', 'Floral Lilás'],
    stock: 18, active: true, featured: true,
    mainImage: '/img/products/admin-prod-vestido.jpg',
    gallery: [],
    sku: 'MCV-VES-002',
    internalNotes: '',
  },
  {
    id: 'prod-3', name: 'Conjunto Casual Menino', category: 'cat-3', collection: 'col-3',
    shortDescription: 'Conjunto camiseta e bermuda, confortável para o dia a dia.',
    fullDescription: 'Conjunto casual composto por camiseta de meia manga em algodão e bermuda em sarja leve. Elástico na cintura para ajuste perfeito. Disponível em diversas combinações de cores. Resistente a múltiplas lavagens.',
    price: 79.90, promotionalPrice: null,
    sizes: ['2', '4', '6', '8', '10', '12'],
    colors: ['Azul Marinho', 'Verde Militar', 'Cinza'],
    stock: 30, active: true, featured: false,
    mainImage: '/img/products/admin-prod-conjunto.jpg',
    gallery: [],
    sku: 'MCV-CNJ-003',
    internalNotes: '',
  },
  {
    id: 'prod-4', name: 'Kit Laços Artesanais', category: 'cat-4', collection: 'col-5',
    shortDescription: 'Kit com 3 laços artesanais em cores variadas.',
    fullDescription: 'Kit exclusivo com 3 laços feitos à mão por artesãs parceiras. Cada laço é confeccionado em fita de cetim de alta qualidade com acabamento delicado. As cores são selecionadas para combinar com diferentes looks.',
    price: 34.90, promotionalPrice: 29.90,
    sizes: ['Único'],
    colors: ['Kit Rosa', 'Kit Neutro', 'Kit Colorido'],
    stock: 40, active: true, featured: true,
    mainImage: '/img/products/admin-prod-lacos.jpg',
    gallery: [],
    sku: 'MCV-LAC-004',
    internalNotes: 'Produto artesanal — prazo de produção de 3 dias.',
  },
  {
    id: 'prod-5', name: 'Macacão Jeans Infantil', category: 'cat-5', collection: 'col-1',
    shortDescription: 'Macacão jeans estiloso, tendência da estação.',
    fullDescription: 'Macacão confeccionado em jeans macio com elastano para conforto. Alças ajustáveis com botões, bolsos funcionais e modelagem moderna. Perfeito para compor looks estilosos em qualquer ocasião.',
    price: 119.90, promotionalPrice: 99.90,
    sizes: ['2', '4', '6', '8'],
    colors: ['Jeans Claro', 'Jeans Médio'],
    stock: 12, active: true, featured: true,
    mainImage: '/img/products/admin-prod-macacao.jpg',
    gallery: [],
    sku: 'MCV-MAC-005',
    internalNotes: 'Lançamento da coleção atual.',
  },
  {
    id: 'prod-6', name: 'Conjunto Pijama Estampado', category: 'cat-1', collection: 'col-2',
    shortDescription: 'Pijama 100% algodão com estampa divertida.',
    fullDescription: 'Pijama duas peças em algodão macio e respirável. Camiseta de manga longa e calça com elástico na cintura. Estampas exclusivas da Maçã Verde que as crianças adoram.',
    price: 69.90, promotionalPrice: null,
    sizes: ['P', 'M', 'G', 'GG'],
    colors: ['Estampa Dinossauro', 'Estampa Unicórnio', 'Estampa Espaço'],
    stock: 22, active: true, featured: false,
    mainImage: '/img/products/admin-prod-pijama.jpg',
    gallery: [],
    sku: 'MCV-PIJ-006',
    internalNotes: '',
  },
];

export const defaultCollections: AdminCollection[] = [
  { id: 'col-1', name: 'Nova Coleção', shortDescription: 'As principais novidades da estação.', coverImage: '/img/collections/admin-col-nova.jpg', active: true, featured: true },
  { id: 'col-2', name: 'Verão Infantil', shortDescription: 'Looks leves e frescos para os dias de sol.', coverImage: '/img/collections/admin-col-verao.jpg', active: true, featured: true },
  { id: 'col-3', name: 'Looks para o dia a dia', shortDescription: 'Conforto e estilo para a rotina.', coverImage: '/img/collections/admin-col-diaadia.jpg', active: true, featured: false },
  { id: 'col-4', name: 'Ocasiões especiais', shortDescription: 'Roupas para festas e eventos.', coverImage: '/img/collections/admin-col-ocasioes.jpg', active: true, featured: true },
  { id: 'col-5', name: 'Acessórios infantis', shortDescription: 'Complementos que fazem a diferença.', coverImage: '/img/collections/admin-col-acessorios.jpg', active: true, featured: false },
];

export const defaultSiteTexts: SiteTexts = {
  heroTitle: 'Moda infantil com conforto, estilo e carinho',
  heroSubtitle: 'Conheça as coleções da Maçã Verde e escolha as peças favoritas para os pequenos.',
  heroPrimaryButton: 'Ver catálogo',
  heroSecondaryButton: 'Falar no WhatsApp',
  heroImage: '/img/banners/admin-hero.jpg',
  aboutTitle: 'Sobre a Maçã Verde',
  aboutText: 'A Maçã Verde veste a infância com peças confortáveis, bonitas e pensadas para o dia a dia das crianças. Cada detalhe é escolhido com carinho, do tecido ao acabamento, para que os pequenos possam brincar, crescer e se expressar com estilo.',
  aboutHighlights: ['Algodão orgânico', 'Produção ética', 'Design exclusivo', 'Entrega em todo Brasil'],
  finalCtaTitle: 'Gostou do que viu?',
  finalCtaText: 'Entre em contato pelo WhatsApp e monte o look perfeito para o seu pequeno.',
  finalCtaButton: 'Falar com a Maçã Verde',
  seoTitle: 'Maçã Verde — Moda Infantil com Conforto e Estilo',
  seoDescription: 'Maçã Verde é uma marca de moda infantil especializada em roupas confortáveis e estilosas para crianças. Conheça nossas coleções e escolha as peças favoritas.',
  seoKeywords: 'moda infantil, roupa de criança, maçã verde, roupa de bebê, acessórios infantis',
};

export const defaultBanners: AdminBanner[] = [
  { id: 'ban-1', title: 'Nova Coleção', subtitle: 'Verão Infantil já disponível', buttonText: 'Ver coleção', buttonLink: '#catalogo', image: '/img/banners/admin-ban-principal.jpg', location: 'principal', active: true },
  { id: 'ban-2', title: 'Acessórios', subtitle: 'Laços artesanais feitos à mão', buttonText: 'Ver acessórios', buttonLink: '#catalogo', image: '/img/banners/admin-ban-colecao.jpg', location: 'colecao', active: true },
  { id: 'ban-3', title: 'Promoção', subtitle: 'Até 20% off em selecionados', buttonText: 'Aproveitar', buttonLink: '#catalogo', image: '/img/banners/admin-ban-promo.jpg', location: 'promocional', active: false },
];

export const defaultStoreInfo: StoreInfo = {
  storeName: 'Maçã Verde',
  whatsapp: '55999260502',
  instagram: '@macaverdeinfantil',
  address: 'Rua das Flores, 123 — Jardim Primavera',
  businessHours: 'Seg a Sex: 9h às 18h | Sáb: 9h às 13h',
  serviceText: 'Atendimento personalizado para ajudar você a escolher as melhores peças.',
  email: 'contato@macaverde.com.br',
  city: 'São Paulo',
  state: 'SP',
  googleMapsLink: 'https://maps.google.com/?q=Rua+das+Flores+123+Sao+Paulo',
  whatsappDefaultMessage: 'Olá! Gostaria de finalizar este pedido na Maçã Verde.',
};

export const defaultOrders: AdminOrder[] = [
  {
    id: 'ped-001', customerName: 'Ana Silva', date: '2026-06-22',
    products: [
      { name: 'Vestido Floral Menina', size: '6', quantity: 1, price: 89.90 },
      { name: 'Kit Laços Artesanais', size: 'Único', quantity: 1, price: 34.90 },
    ],
    total: 124.80, status: 'finalizado',
  },
  {
    id: 'ped-002', customerName: 'Carla Oliveira', date: '2026-06-22',
    products: [
      { name: 'Body Algodão Bebê', size: 'M', quantity: 2, price: 49.90 },
      { name: 'Conjunto Pijama Estampado', size: 'G', quantity: 1, price: 69.90 },
    ],
    total: 169.70, status: 'em-atendimento',
  },
  {
    id: 'ped-003', customerName: 'Mariana Santos', date: '2026-06-23',
    products: [
      { name: 'Macacão Jeans Infantil', size: '4', quantity: 1, price: 119.90 },
    ],
    total: 119.90, status: 'novo',
  },
  {
    id: 'ped-004', customerName: 'Juliana Costa', date: '2026-06-23',
    products: [
      { name: 'Conjunto Casual Menino', size: '8', quantity: 1, price: 79.90 },
    ],
    total: 79.90, status: 'novo',
  },
  {
    id: 'ped-005', customerName: 'Fernanda Lima', date: '2026-06-21',
    products: [
      { name: 'Vestido Floral Menina', size: '4', quantity: 1, price: 89.90 },
      { name: 'Kit Laços Artesanais', size: 'Único', quantity: 2, price: 34.90 },
      { name: 'Body Algodão Bebê', size: 'P', quantity: 1, price: 49.90 },
    ],
    total: 209.60, status: 'cancelado',
  },
];

export const defaultSettings: AdminSettings = {
  adminName: 'Administrador',
  adminEmail: 'admin@macaverde.com.br',
  primaryColor: '#4A8C5C',
  secondaryColor: '#E8C4A2',
  showPrices: true,
  showStock: true,
  showFeatured: true,
  whatsappNumber: '55999260502',
  cartFinalMessage: 'Olá! Gostaria de finalizar este pedido na Maçã Verde.',
};

export const USER_EMAIL = 'admin@macaverde.com.br';
export const USER_PASSWORD = '123456';